// Field-level merge, a line-for-line port of `JSONMerge` (GroundTeam/Sync/SyncMergeBase.swift:62-100), WEB_PLAN.md §3.7.
// Phones run exactly this rule when a teammate's edit meets theirs, so the web must decide the same way or two
// clients would settle on different values for the same item.

import type { Json, JsonObject } from '@/contracts/wire';
import { UUID_KEYED_FIELDS } from './canonical';

const has = (o: object, k: string) => Object.prototype.hasOwnProperty.call(o, k);

/**
 * Structural equality of parsed JSON, like `NSObject.isEqual` on JSONSerialization output: numbers by value (so `1`
 * equals `1.0`), strings by code units (case matters, which is why the web never re-cases a value it did not edit),
 * arrays in order, objects by key set. A UUID-keyed field (`customValues`) is compared as a set of key/value pairs, so
 * two encodes of the same dictionary in different hash orders are equal. `undefined` (a missing key) equals only
 * `undefined`; it never equals `null`.
 */
export function deepEqual(a: Json | undefined, b: Json | undefined): boolean {
  if (a === b) return true;
  if (a === undefined || b === undefined || a === null || b === null) return false;
  if (typeof a !== typeof b) return false;
  if (typeof a === 'number') return a === b; // NaN never reaches JSON
  if (typeof a !== 'object') return false; // strings / booleans already compared by ===
  const aArr = Array.isArray(a);
  if (aArr !== Array.isArray(b)) return false;
  if (aArr) {
    const bArr = b as Json[];
    if (a.length !== bArr.length) return false;
    for (let i = 0; i < a.length; i++) if (!deepEqual(a[i], bArr[i])) return false;
    return true;
  }
  const ao = a as JsonObject;
  const bo = b as JsonObject;
  const aKeys = Object.keys(ao);
  if (aKeys.length !== Object.keys(bo).length) return false;
  for (const key of aKeys) {
    if (!has(bo, key)) return false;
    if (!fieldEqual(key, ao[key], bo[key])) return false;
  }
  return true;
}

/** Equality of one field's values; UUID-keyed flat arrays compare as sets of pairs. */
export function fieldEqual(key: string, a: Json | undefined, b: Json | undefined): boolean {
  if (UUID_KEYED_FIELDS.includes(key)) {
    const pa = pairsOf(a);
    const pb = pairsOf(b);
    if (pa && pb) return pairSetsEqual(pa, pb);
  }
  return deepEqual(a, b);
}

/** A flat `[key, value, …]` array as pairs, or null when it is not that shape (then it compares structurally). */
function pairsOf(value: Json | undefined): Map<string, Json> | null {
  if (!Array.isArray(value) || value.length % 2 !== 0) return null;
  const map = new Map<string, Json>();
  for (let i = 0; i < value.length; i += 2) {
    const k = value[i];
    if (typeof k !== 'string' || map.has(k)) return null;
    map.set(k, value[i + 1] as Json);
  }
  return map;
}

function pairSetsEqual(a: Map<string, Json>, b: Map<string, Json>): boolean {
  if (a.size !== b.size) return false;
  for (const [k, v] of a) {
    if (!b.has(k) || !deepEqual(v, b.get(k))) return false;
  }
  return true;
}

/**
 * `JSONMerge.threeWay`: three-way merge of top-level fields.
 * - A field changed on one side only takes that side's value.
 * - A field changed on both sides to different values takes the newer side (`localIsNewer`).
 * - Without a base, every differing field is decided by recency.
 * - A winner that is `undefined` (the key was removed) leaves the key out.
 * Nested objects are values: they are never merged inside, exactly as on iOS.
 */
export function threeWay(
  base: JsonObject | null | undefined,
  local: JsonObject,
  server: JsonObject,
  localIsNewer: boolean,
): JsonObject {
  const keys = new Set<string>(Object.keys(local));
  for (const k of Object.keys(server)) keys.add(k);
  if (base) for (const k of Object.keys(base)) keys.add(k);

  const merged: JsonObject = {};
  for (const key of keys) {
    const l = has(local, key) ? local[key] : undefined;
    const s = has(server, key) ? server[key] : undefined;
    let localChanged: boolean;
    let serverChanged: boolean;
    if (base) {
      const b = has(base, key) ? base[key] : undefined;
      localChanged = !fieldEqual(key, l, b);
      serverChanged = !fieldEqual(key, s, b);
    } else {
      localChanged = !fieldEqual(key, l, s);
      serverChanged = localChanged;
    }
    let winner: Json | undefined;
    if (localChanged && serverChanged && !fieldEqual(key, l, s)) {
      winner = localIsNewer ? l : s;
    } else if (localChanged) {
      winner = l;
    } else {
      winner = s;
    }
    if (winner !== undefined) merged[key] = winner;
  }
  return merged;
}
